// StatsigClientUtils.ts
import { Quote } from "../types/Quote.interface";
import { StateUtils } from "./StateUtils";

export interface StatsigCustomUserAttributes {
    priorityCodeLowerCase: string;
    quoteHasDogs: boolean;
    quoteHasCats: boolean;
    quotePetCount: number;
    breedsInQuote?: string;
    /**
     * ENG-20300: the quote's rating state (US state / CA province code, upper case), for state-targeted
     * experiments such as the `forms-main-ca-quoters` gate. Console rules read it by name — renaming it
     * breaks them silently.
     */
    ratingState?: string;
}

// Define the interface based on the old ClientInitializeResponse structure
interface InitializeResponse {
    name: string;
    value: unknown;
    group: string;
    rule_id: string;
    is_device_based: boolean;
    secondary_exposures: Record<string, string>[];
    is_experiment_active?: boolean;
    is_user_in_experiment?: boolean;
    is_in_layer?: boolean;
    allocated_experiment_name?: string;
    explicit_parameters?: string[];
    undelegated_secondary_exposures?: Record<string, string>[];
}

interface ClientInitializeResponse {
    feature_gates: Record<string, InitializeResponse>;
    dynamic_configs: Record<string, InitializeResponse>;
    layer_configs: Record<string, InitializeResponse>;
    sdkParams: Record<string, unknown>;
    has_updates: boolean;
    generator: string;
    sdkInfo: {
        sdkType: string;
        sdkVersion: string;
    };
    time: number;
    evaluated_keys: Record<string, unknown>;
    hash_used: string;
    user: any;
}

const LAYER_CONFIG_MAP = {
    "forms-main": "774299799",
    "forms-dog-experiments": "1529909095",
    "forms-cat-experiments": "3285938945",
    "post-purchase-main": "1740475544"
} as const;

type LayerName = keyof typeof LAYER_CONFIG_MAP;

// Client-safe utility functions for Statsig that do not expose PrivateConfig
export class StatsigClientUtils {
    /**
     * Normalizes an account number to its base form for the cross-site `accountID`
     * Statsig unit (ENG-17341/ENG-18844): account numbers gain a policy-version
     * suffix (SP7520269-0004) as add-a-pet versions the policy, while the Member
     * Center hashes the BASE form. Every place that installs `customIDs.accountID`
     * — server bootstrap AND the client-side fallback update — must use this, or
     * the two surfaces attribute exposures to different units.
     */
    static normalizeAccountId(accountId: string | undefined): string | undefined {
        return accountId?.replace(/-\d+$/, "");
    }

    static getStatsigQuoteAttributes({ quote, normalizedPriorityCode }: { quote?: Quote; normalizedPriorityCode?: string }): StatsigCustomUserAttributes {
        const priorityCodeLowerCase = normalizedPriorityCode || quote?.discountCode?.toLowerCase() || quote?.affiliateCode?.toLowerCase() || "spot";
        const quoteHasDogs = !!quote?.policies?.some(policy => policy?.species === "Dog");
        const quoteHasCats = !!quote?.policies?.some(policy => policy?.species === "Cat");
        const quotePetCount = quote?.policies?.length ?? 0;
        const breedsInQuote = quote?.policies
            ?.map(policy => policy?.breedName)
            .filter((breedName): breedName is string => breedName !== undefined)
            ?.join(",");

        return {
            priorityCodeLowerCase: priorityCodeLowerCase,
            quoteHasDogs: quoteHasDogs,
            quoteHasCats: quoteHasCats,
            quotePetCount: quotePetCount,
            breedsInQuote: breedsInQuote,
            ratingState: StateUtils.normalizeStateCode(quote?.ratingAddress?.state)
        };
    }

    static getLayerParam<T>(data: string | ClientInitializeResponse, layerName: LayerName, paramName: string): T | undefined {
        try {
            // Parse the data if it's a string (new format from @statsig/statsig-node-core)
            let parsedData: ClientInitializeResponse;
            if (typeof data === "string") {
                parsedData = JSON.parse(data);
            } else {
                parsedData = data;
            }

            const layerId = LAYER_CONFIG_MAP[layerName];
            const config = parsedData.layer_configs[layerId];

            if (!config?.value || typeof config.value !== "object" || config.value === null) {
                return undefined;
            }

            const value = (config.value as Record<string, unknown>)[paramName];
            return value as T | undefined;
        } catch (error) {
            return undefined;
        }
    }
}
